import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { CommonStoreManager } from 'src/app/common/store/common.store-manager';
import { ModuleConfigService } from 'src/app/common/services/module-config/module-config.service';
import { isEqual } from 'lodash';
import moment from 'moment';

import { FilesService } from 'src/app/common/services/files.service';

import { MarkdownService } from '../../../common/components/markdown-editor/markdown.service';
import { HolAttachments, HolisFile } from '../../../common/models/hol-attachments.model';
import { HolContext } from '../../../common/models/hol-context.model';
import { HolNotification } from '../../../common/models/hol-notification.model';
import { HolTag } from '../../../common/models/hol-tag';
import { NotificationsService } from '../../../common/services/notifications/notifications.service';
import { ParseMapperService } from '../../../common/services/parse-mapper.service';
import { RequestService } from '../../../common/services/request.service';
import { OclDecision } from '../../models/ocl-decision.model';
import { OclHistoryLog } from '../../models/ocl-history-log.model';
import { OclDecisionsStoreManager } from '../../store/decisions/ocl-decisions.store-manager';
import { OclDecisionTagService } from '../ocl-decision-tag-service/ocl-decision-tag.service';
import { OclHistoryService } from '../ocl-history-service/ocl-history.service';
import { OclMailService } from '../ocl-mail-service/ocl-mail.service';
import { OclOptionsService } from '../ocl-options-service/ocl-options.service';
import { OclSmsService } from '../ocl-sms-service/ocl-sms.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export abstract class OclDecisionService<T extends OclDecision = OclDecision, U extends HolContext = HolContext> {
  public dateTo: moment.Moment;
  public totalDecisions: number;
  // tslint:disable:variable-name
  protected ParseDecision;
  protected ParseEvent;
  protected ParseDecisionTag;
  protected ParseTag;
  protected ParseFlight = Parse.Object.extend('GOCFlight');
  protected ParseErpDecision = Parse.Object.extend('GDCDecisions');
  protected ParseGocFlightLogBook = Parse.Object.extend('GOCFlightLogbook');
  // tslint:enabled
  protected _decisions: T[]; // @cache
  protected loadMoreCount = 1;
  private ParseUser = Parse.Object.extend('_User');
  protected readonly translate = inject(TranslateService);

  protected constructor(
    protected readonly datePipe: DatePipe,
    protected readonly filesService: FilesService,
    protected decisionsStoreManager: OclDecisionsStoreManager,
    protected parseMapper: ParseMapperService,
    protected requestService: RequestService,
    protected optionsService: OclOptionsService,
    protected notificationsService: NotificationsService,
    protected smsService: OclSmsService,
    protected mailService: OclMailService,
    protected historyService: OclHistoryService<OclHistoryLog>,
    protected decisionTagService: OclDecisionTagService,
    protected moduleConfig: ModuleConfigService,
    protected markdownService: MarkdownService,
    protected commonStoreManager: CommonStoreManager,
  ) {}

  public async getAll(
    forceToRefresh: boolean,
    loadMore: boolean = false,
    isFromPooling?: boolean,
    isPrivate?: boolean,
    filterDataStartDate?: Date,
  ): Promise<T[]> {
    if (loadMore) {
      this.loadMoreCount = this.loadMoreCount + 1;
    } else if (!isFromPooling) {
      this.loadMoreCount = 1;
    }
    if (this._decisions !== undefined && this._decisions.length && !forceToRefresh) {
      return Promise.resolve(this._decisions);
    } else {
      const decisionsToDisplay = this.optionsService.getDecisionsToDisplay();
      const query = new Parse.Query(this.ParseDecision);

      let simpleQuery;
      let dateFrom;
      let dateTo;

      if (this.moduleConfig.config.canChooseDataStartDate && filterDataStartDate instanceof Date && filterDataStartDate !== undefined) {
        dateFrom = moment.utc(filterDataStartDate).endOf('day');
        dateTo = moment.utc(filterDataStartDate).startOf('day');
      } else {
        if (decisionsToDisplay) {
          dateFrom = isFromPooling ? moment.utc() : moment.utc().subtract(decisionsToDisplay * (this.loadMoreCount - 1), 'hours');
          dateTo = moment.utc().subtract(decisionsToDisplay * this.loadMoreCount, 'hours');
        }
      }
      if (!!dateFrom && !!dateTo) {
        this.dateTo = dateTo;
        query.doesNotExist('customCreatedAt');
        query.lessThanOrEqualTo('createdAt', dateFrom.toDate());
        query.greaterThanOrEqualTo('createdAt', dateTo.toDate());

        const query2 = new Parse.Query(this.ParseDecision);
        query2.exists('customCreatedAt');
        query2.notEqualTo('customCreatedAt', '');
        query2.lessThanOrEqualTo('customCreatedAt', dateFrom.toDate());
        query2.greaterThanOrEqualTo('customCreatedAt', dateTo.toDate());

        simpleQuery = Parse.Query.or(query, query2);
      } else {
        simpleQuery = query;
      }

      const queryPinned = new Parse.Query(this.ParseDecision);
      queryPinned.descending('createdAt');
      queryPinned.equalTo('isPinned', true);

      const decisionQuery = this.getAdditionnalQueries(simpleQuery, queryPinned, filterDataStartDate);

      decisionQuery.descending('createdAt');

      if (isPrivate) {
        decisionQuery.notEqualTo('isPrivate', true);
      }
      decisionQuery.include('flight');
      decisionQuery.include('createdBy');
      decisionQuery.include('applFlights');
      decisionQuery.include('event.scenario');
      decisionQuery.include('erpDecision');
      decisionQuery.doesNotExist('isFromFlight');

      const totalQuery = new Parse.Query(this.ParseDecision);
      const [results, totalCount] = await Promise.all([
        this.requestService.performFindAllQuery(decisionQuery),
        this.requestService.performCountQuery(totalQuery),
      ]);

      this.totalDecisions = totalCount;
      const decisionTagQuery = new Parse.Query(this.ParseDecisionTag);
      decisionTagQuery.include('tag');
      decisionTagQuery.descending('createdAt');
      // decisionTagQuery.matchesQuery('decision', decisionQuery);
      decisionTagQuery.containedIn('decision', results);
      const decisionTags = await this.requestService.performFindQuery(decisionTagQuery);
      const decisions = [];
      for (const decision of results) {
        const tags = this.getTagsForDecision(decisionTags, decision);
        decisions.push(this.newDecision(decision, tags));
      }
      this._decisions = decisions;
      return decisions;
    }
  }

  public create(
    decision: T,
    notifications: HolNotification[],
    context?: U,
    duplicateToOtherModule?: boolean,
    isTransformFromLogbook?: boolean,
  ): Promise<T> {
    let addBreakLinesBefore = false;
    if (decision.attachments && decision.attachments.note) {
      addBreakLinesBefore = true;
    }

    const currentUser = Parse.User.current();

    const addressMailToSend = this.notificationsService.getAddressMailToSend(notifications);
    const phoneNumbersToSend = this.notificationsService.getPhoneNumbersToSend(notifications);

    const parseDecision = new this.ParseDecision();
    parseDecision.setACL(decision.acl);
    parseDecision.set('message', decision.message);
    parseDecision.set('nextInfoTime', decision.nextInfoTime);
    parseDecision.set('isPinned', decision.isPinned);
    parseDecision.set('done', decision.done);
    parseDecision.set('doneBy', Parse.User.current());
    parseDecision.set('isTodo', decision.isTodo ? decision.isTodo : false);
    if (decision.attachments) {
      parseDecision.set('attachments', JSON.stringify(decision.attachments));
    }
    if (decision.event && decision.event.objectId !== null) {
      parseDecision.set(
        'event',
        new this.ParseEvent({
          id: decision.event.objectId,
        }),
      );
    }
    if (decision.flight && decision.flight.objectId !== null) {
      parseDecision.set(
        'flight',
        new this.ParseFlight({
          id: decision.flight.objectId,
        }),
      );
    }
    if (decision.toERP) {
      parseDecision.set('toERP', decision.toERP);
    }
    if (decision.toGOC) {
      parseDecision.set('toGOC', decision.toGOC);
    }
    if (isTransformFromLogbook) {
      parseDecision.set('customCreatedAt', decision.customCreatedAt ? decision.customCreatedAt : decision.createdAt);
      parseDecision.set('createdBy', new this.ParseUser({ id: decision.createdBy.objectId }));
    } else {
      parseDecision.set('createdBy', currentUser);
    }

    this.setAdditionalFields(decision, parseDecision);

    return this.requestService.performSaveQuery(parseDecision).then(async d => {
      let savedDecision;
      if (
        decision.attachments &&
        decision.attachments.noteFile &&
        decision.attachments.note &&
        (context.htmlTitle === '' || !context.htmlTitle)
      ) {
        const content = decision.attachments.note;
        const nameFile = decision.attachments.noteFile.fileName;
        context.htmlTitle = decision.message;
        const htmlContent = this.markdownService.parseMdToHtml(content);
        const htmlTemplate = this.markdownService.createHtmlContent(htmlContent, context);

        const blob = new Blob([htmlTemplate], { type: 'text/html' });
        const reader = new FileReader();

        savedDecision = await new Promise((resolve, reject) => {
          reader.readAsDataURL(blob);
          reader.onloadend = async () => {
            await this.filesService.uploadFile(nameFile, { base64: reader.result }).then(
              async url => {
                decision.attachments.noteFile.url = url;
                decision.attachments.noteFile.fileName = nameFile;
                decision.attachments.note = content;
                //decision.attachments.noteFile = decision.attachments.noteFile;
                const parseUpdateDecision = new this.ParseDecision();
                parseUpdateDecision.id = d.id;
                parseUpdateDecision.set('attachments', JSON.stringify(decision.attachments));
                await this.requestService.performSaveQuery(parseUpdateDecision).then(ud => resolve(ud));
              },
              err => {
                reject(err);
              },
            );
          };
        });
      } else {
        savedDecision = d;
      }

      let parseTags;
      if (decision.tags) {
        const decisionTags = decision.tags.map(tag => {
          return new this.ParseDecisionTag({
            decision: savedDecision,
            tag: new this.ParseTag({ id: tag && tag.objectId }),
          });
        });
        parseTags = await this.requestService.performSaveAllQuery(decisionTags);
      }

      const newStatusDecisions = this.newDecision(savedDecision, parseTags);

      if (decision.isTodo) {
        return this.nextInfoHistoryInNotes(newStatusDecisions, context, true, true, false, addBreakLinesBefore).then(updatedAttachments => {
          decision.attachments = updatedAttachments;
          const parseDecision = new this.ParseDecision();
          parseDecision.id = newStatusDecisions.objectId;
          parseDecision.set('attachments', JSON.stringify(updatedAttachments));
          return this.requestService.performSaveQuery(parseDecision).then(updatedDecision => {
            const newUpdatedDecision = this.newDecision(updatedDecision, updatedDecision.tags);
            return this.afterSave(
              decision,
              newUpdatedDecision,
              updatedDecision,
              addressMailToSend,
              phoneNumbersToSend,
              duplicateToOtherModule,
            );
          });
        });
      }

      return this.afterSave(
        decision,
        newStatusDecisions,
        savedDecision,
        addressMailToSend,
        phoneNumbersToSend,
        duplicateToOtherModule,
        isTransformFromLogbook,
      );
    });
  }

  public afterSave(
    decision: T,
    newDecision: T,
    parseDecision: Parse.Object,
    addressMailToSend: string[],
    phoneNumbersToSend: string[],
    duplicateToOtherModule?: boolean,
    isTransformFromLogbook?: boolean,
  ) {
    if (addressMailToSend.length) {
      this.mailService.sendNewDecisionMail(newDecision, addressMailToSend);
    }
    if (phoneNumbersToSend.length) {
      this.smsService.sendNewDecisionSMS(newDecision, phoneNumbersToSend);
    }

    if (duplicateToOtherModule) {
      this.duplicateDecisionToOtherModule(newDecision);
    }
    this.historyService
      .postLog(
        OclHistoryLog.create(
          this.getLogMessage({
            message: decision.message,
            nextInfoTime: decision.nextInfoTime,
          }),
          'decision',
          isTransformFromLogbook ? 'transform from logbook' : 'create',
          decision.attachments,
          newDecision,
          parseDecision.getACL(),
          parseDecision,
        ),
      )
      .then();
    // STORE
    this.decisionsStoreManager.addOneDecision(newDecision);
    return newDecision;
  }

  public delete(objectId: string): Promise<T> {
    const parseDecision = new this.ParseDecision({ id: objectId });
    return this.requestService.performDestroyQuery(parseDecision, async () => {
      // STORE
      this.decisionsStoreManager.deleteOneDecison(objectId);
      //await this.historyService.postLog(OclHistoryLog.create(OclLogbookService._getLogMessage(logbook), 'logbook', 'delete'));
    });
  }

  public update(
    decision: T,
    notifications: HolNotification[],
    context: U,
    oldDuplicateToOtherModuleValue?: boolean,
    newDuplicateToOtherModuleValue?: boolean,
  ): Promise<T> {
    const addressMailToSend = this.notificationsService.getAddressMailToSend(notifications);
    const phoneNumbersToSend = this.notificationsService.getPhoneNumbersToSend(notifications);

    const oldDecision = new OclDecision(new this.ParseDecision({ id: decision.objectId }));
    const oldNIvalue = oldDecision.nextInfoTime;
    const oldNIdone = oldDecision.done;

    const parseDecision = new this.ParseDecision();
    parseDecision.id = decision.objectId;
    parseDecision.setACL(decision.acl);
    parseDecision.set('attachments', JSON.stringify(decision.attachments));
    parseDecision.set('isPinned', decision.isPinned);
    parseDecision.set('isTodo', decision.isTodo);
    parseDecision.set('nextInfoTime', decision.nextInfoTime || undefined);

    parseDecision.set('done', decision.done);
    parseDecision.set('doneBy', Parse.User.current());

    this.setAdditionalFields(decision, parseDecision);
    if (decision.event && decision.event.objectId !== null) {
      parseDecision.set(
        'event',
        new this.ParseEvent({
          id: decision.event.objectId,
        }),
      );
    } else {
      parseDecision.set('event', null);
    }
    if (decision.flight && decision.flight.objectId !== null) {
      parseDecision.set(
        'flight',
        new this.ParseFlight({
          id: decision.flight.objectId,
        }),
      );
    } else {
      parseDecision.set('flight', null);
    }
    if (decision.message) {
      parseDecision.set('message', decision.message);
    }
    if (decision.customCreatedAt) {
      parseDecision.set('customCreatedAt', decision.customCreatedAt);
    }
    parseDecision.set('toERP', decision.toERP);
    parseDecision.set('toGOC', decision.toGOC);

    return this.requestService.performSaveQuery(parseDecision).then(res => {
      return this.decisionTagService.updateTags(decision).then(async newTags => {
        const newDecision = this.newDecision(res, newTags);
        if (
          decision.isTodo &&
          decision.nextInfoTime &&
          (!moment(oldNIvalue).isSame(decision.nextInfoTime) || oldNIdone !== decision.done)
        ) {
          let isTodoChangeDate = false;
          let isDoneCheck = false;
          if (oldNIdone !== decision.done && !!decision.done) {
            isDoneCheck = true;
          }
          if (decision.done) {
            isDoneCheck = true;
          }
          if (!moment(oldNIvalue).isSame(decision.nextInfoTime)) {
            isTodoChangeDate = true;
          }
          return this.nextInfoHistoryInNotes(newDecision, context, false, isTodoChangeDate, isDoneCheck).then(updatedAttachments => {
            decision.attachments = updatedAttachments;
            const parseDecision = new this.ParseDecision();
            parseDecision.id = newDecision.objectId;
            parseDecision.set('attachments', JSON.stringify(updatedAttachments));
            return this.requestService.performSaveQuery(parseDecision).then(updatedDecision => {
              const newUpdatedDecision = this.newDecision(updatedDecision, updatedDecision.tags);
              return this.afterUpdate(
                decision,
                newUpdatedDecision,
                updatedDecision,
                addressMailToSend,
                phoneNumbersToSend,
                newDuplicateToOtherModuleValue,
                oldDuplicateToOtherModuleValue,
              );
            });
          });
        }

        return this.afterUpdate(
          decision,
          newDecision,
          res,
          addressMailToSend,
          phoneNumbersToSend,
          newDuplicateToOtherModuleValue,
          oldDuplicateToOtherModuleValue,
        );
      });
    });
  }

  public afterUpdate(
    decision: T,
    newDecision: T,
    parseDecision: Parse.Object,
    addressMailToSend: string[],
    phoneNumbersToSend: string[],
    newDuplicateToOtherModuleValue?: boolean,
    oldDuplicateToOtherModuleValue?: boolean,
  ) {
    if (addressMailToSend.length) {
      this.mailService.sendNewDecisionMail(newDecision, addressMailToSend);
    }
    if (phoneNumbersToSend.length) {
      this.smsService.sendNewDecisionSMS(newDecision, phoneNumbersToSend);
    }

    this.historyService.postLog(
      OclHistoryLog.create(
        this.getLogMessage({
          message: decision.message,
          nextInfoTime: decision.nextInfoTime,
        }),
        'decision',
        'update',
        decision.attachments,
        decision,
        decision.acl,
        parseDecision,
      ),
    );

    if (!isEqual(newDuplicateToOtherModuleValue, oldDuplicateToOtherModuleValue)) {
      // Supprimer
      if (!newDuplicateToOtherModuleValue && oldDuplicateToOtherModuleValue) {
        this.deleteDuplicateDecisionFromModule(newDecision);
      }
      // Ajouter
      if (newDuplicateToOtherModuleValue && !oldDuplicateToOtherModuleValue) {
        this.duplicateDecisionToOtherModule(newDecision);
      }
    }
    // STORE
    // OccDecisionsStoreManager.updateOneDecision(newDecision)

    return newDecision;
  }

  public markAsDone(decision: T, done: boolean, context?: U): Promise<T> {
    const parseDecision = new this.ParseDecision();
    parseDecision.id = decision.objectId;
    parseDecision.set('done', done);
    parseDecision.set('doneBy', Parse.User.current());

    return this.requestService.performSaveQuery(parseDecision).then(parseData => {
      const newDecision = this.newDecision(parseData);
      if (newDecision.isTodo && !!done) {
        // generate note with new next info done
        return this.nextInfoHistoryInNotes(newDecision, context, false, false, true).then(updatedAttachments => {
          const parseDecisionUpdate = new this.ParseDecision();
          parseDecisionUpdate.id = newDecision.objectId;
          parseDecisionUpdate.set('attachments', JSON.stringify(updatedAttachments));
          return this.requestService.performSaveQuery(parseDecisionUpdate).then(updatedParseData => {
            newDecision.attachments = updatedAttachments;
            this.historyService.postLog(
              OclHistoryLog.create(
                this.getLogMessage({
                  message: newDecision.message,
                  nextInfoTime: newDecision.nextInfoTime,
                }),
                'decision',
                newDecision.done ? 'done' : 'backtodo',
                null,
                null,
                newDecision.acl,
                updatedParseData,
              ),
            );
            return newDecision;
          });
        });
      } else {
        this.historyService.postLog(
          OclHistoryLog.create(
            this.getLogMessage({
              message: newDecision.message,
              nextInfoTime: newDecision.nextInfoTime,
            }),
            'decision',
            newDecision.done ? 'done' : 'backtodo',
            newDecision.attachments,
            newDecision,
            newDecision.acl,
            parseData,
          ),
        );

        return newDecision;
      }
    });
  }

  public fetchNewData(isFromPooling: boolean = false, filterDataStartDate?: Date) {
    return this.getAll(true, false, isFromPooling, false, filterDataStartDate).then(decisions => {
      // STORE
      this.decisionsStoreManager.updateDecisionsFromPooling(decisions, this.moduleConfig.config.moduleName);
    });
  }

  public nextInfoHistoryInNotes(
    decision: OclDecision,
    context?: HolContext,
    isTodoCreation = false,
    isTodoChangeDate = false,
    isDoneCheck = false,
    addMdBreakLinesBefore = false,
  ) {
    let content = '';
    let nameFile;
    const dateFormat = 'DD/MM/YY HH:mm[Z]';
    const attachments = decision.attachments ? decision.attachments : new HolAttachments();
    const noteFile: HolisFile = new HolisFile();

    context.htmlTitle = context.htmlTitle ? context.htmlTitle : decision.message;

    if (decision.attachments && decision.attachments.note) {
      content += decision.attachments.note;
      nameFile = decision.attachments.noteFile.fileName;
    } else {
      nameFile = `note-${this.moduleConfig.config.moduleName.toLocaleLowerCase()}-${context.category
        .substring(0, 3)
        .toLocaleLowerCase()
        .replace(/é|è|ê/g, 'e')}-${moment().utc().format('DD-MM-YYYY')}.html`;
      addMdBreakLinesBefore = true;
    }

    content += addMdBreakLinesBefore ? '\n' + '\n' + '\n' + '---' + '\n' + '\n' + '\n' : '';

    if (isTodoChangeDate) {
      const txt1: string = this.translate.instant('NEXT_INFO.NEW_NI', {
        date: moment(decision.nextInfoTime).format(dateFormat),
      });
      content += '- **';
      content += isTodoCreation
        ? this.translate.instant('NEXT_INFO.CREATE_NI', {
            date: moment().utc().format(dateFormat),
          })
        : this.translate.instant('NEXT_INFO.EDIT_NI', {
            date: moment().utc().format(dateFormat),
          });
      content += '**' + ' ' + txt1 + ' ' + '\n';
    }
    if (isDoneCheck) {
      const txt2: string = this.translate.instant('NEXT_INFO.DONE_BY', {
        firstName: decision.updatedBy.firstName ? decision.updatedBy.firstName : decision.createdBy.firstName,
        lastName: decision.updatedBy.lastName ? decision.updatedBy.lastName : decision.createdBy.lastName,
      });
      content += '- **';
      content += this.translate.instant('NEXT_INFO.DONE_NI', {
        date: moment().utc().format(dateFormat),
      });
      content += '**' + ' ' + txt2 + ' ' + '\n';
    }

    const mdTemplate = content;

    const htmlContent = this.markdownService.parseMdToHtml(mdTemplate);
    const htmlTemplate = this.markdownService.createHtmlContent(htmlContent, context);

    const blob = new Blob([htmlTemplate], { type: 'text/html' });
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise((resolve, reject) => {
      reader.onloadend = () => {
        this.filesService.uploadFile(nameFile, { base64: reader.result }).then(
          url => {
            noteFile.url = url;
            noteFile.fileName = nameFile;
            attachments.note = mdTemplate;
            attachments.noteFile = noteFile;
            resolve(attachments);
          },
          err => {
            reject(err);
          },
        );
      };
    });
  }

  deleteErpDecision(oclDecision: T): void {
    const parseOclLogbook = new this.ParseDecision({ id: oclDecision.objectId });
    const parseErpLogbook = new this.ParseErpDecision({ id: oclDecision.erpDecision.objectId });
    parseErpLogbook.set('toECL', false);
    this.requestService.performSaveQuery(parseErpLogbook).then(() => {
      this.requestService.performDestroyQuery(
        parseOclLogbook,
        () => {
          // STORE
          this.decisionsStoreManager.deleteOneDecison(oclDecision.objectId);
        },
        error => {
          console.log(error);
        },
      );
    });
  }

  protected getTagsForDecision(decisionsTags: Parse.Object[], decision: Parse.Object): Parse.Object[] {
    return decisionsTags.filter(dt => {
      return dt.get('decision').id === decision.id;
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected setAdditionalFields(decision: T, parseDecision: Parse.Object) {}

  protected newDecision(parseObject?: Parse.Object, tags?: Parse.Object[]): T {
    return new OclDecision(parseObject, tags && tags.map(t => new HolTag(t.get('tag')))) as T;
  }

  protected getAdditionnalQueries(query, queryPinned, filterDataStartDate) {
    return Parse.Query.or(query, queryPinned);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected duplicateDecisionToOtherModule(decision: T) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected deleteDuplicateDecisionFromModule(decision: T) {}

  private getLogMessage(statusDecision) {
    if (!statusDecision) {
      return;
    }

    if (statusDecision.nextInfoTime) {
      const filteredDate = this.datePipe.transform(new Date(statusDecision.nextInfoTime), 'EEE dd MMM');
      return statusDecision.message + '<br/> Next info time: ' + filteredDate;
    }

    return statusDecision.message;
  }
}
